import * as React from 'react';
import {
  Paper,
  Typography,
  createStyles,
  makeStyles,
  Theme,
  Snackbar,
  Box,
  Button,
  Divider,
} from '@material-ui/core';

import { Alert } from '@material-ui/lab';
import { DailyReport, DailyReportModel } from '../../../models/DailyReport/DailyReportModel';
import { DailyReportReadModel } from '../../../models/DailyReport/DailyReportReadModel';
import { displayDate, displayToYearMonthDay } from '../../../util/DateUtil';
import { formatLocalNumber } from '../../../util/Math';
import DailyReportCommentList from './DailyReportCommentList';
import DailyReportInvoiceList from './DailyReportInvoiceList';
import { useState, useEffect } from 'react';

import { LocalStorageService } from '../../../services/LocalStorageService.';
import DeleteButton from '../DeleteButton';
import {
  DailyReportCommentOpenContext,
  DailyReportsContext,
} from '../../../screens/dailyReport/DailyReportScreen';
import NotificationToast from '../NotificationToast';
import ReadAllStatusButton from '../ReadAllStatusButton';
import { DailyReportComment } from '../../../models/DailyReport/DailyReportCommentModel';
import { DailyReportUtil } from '../../../util/DailyUtil';
import { FontAwesome } from '@expo/vector-icons';
import { set } from 'date-fns';
import { Invoice } from '../../../models/InvoiceModel';
import { ReceiptService } from '../../../models/invoice/receipt.service';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      marginTop: theme.spacing(0), // 20px相当
      backgroundColor: 'white',
      padding: theme.spacing(0.5),
    },
  })
);

interface DailyReportItemProps {
  dailyReport: DailyReport;
}

/**
 * 日報一覧アイテム
 */
const DailyReportItem = (props: DailyReportItemProps) => {
  const { showComments } = React.useContext(DailyReportCommentOpenContext);
  const { dailyReports, setDailyReports } = React.useContext(DailyReportsContext);
  const [dailyReport, setDailyReport] = useState<DailyReport>(props.dailyReport);

  const user = dailyReport.user;
  const [invoices, setInvoices] = useState<Invoice[]>(dailyReport.invoices);

  const comments = dailyReport.comments;

  // const { comments, setComments } = React.useContext(DailyReportContext);
  // const comments = dailyReport.comments;

  // const [comments, setComments] = useState<DailyReportComment[]>(dailyReport.comments);
  const [isReading, setIsReading] = useState(false);
  const [isRead, setIsRead] = useState(dailyReport.is_read || false);
  const [showComment, setShowComment] = useState(showComments);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const myUser = LocalStorageService.getUser();
  //自分自身の日報かのフラグ
  const isMyDailyReport = dailyReport.user_id === myUser?.id;

  const classes = useStyles();

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const receiptService = new ReceiptService();
  const sumTotalAmount = () => {
    const totalAmount = invoices.reduce((total, invoice) => {
      return total + invoice.total;
    }, 0);
    return formatLocalNumber(totalAmount);
  };

  // コメントの改行を反映させる関数
  const formatCommentText = (text: string) => {
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));
  };

  const handleDelete = async () => {
    try {
      await DailyReportModel.delete(dailyReport);
      //削除した日報を配列から削除する
      setDailyReports(
        dailyReports.filter(existingInvoice => {
          return existingInvoice.id !== dailyReport.id;
        })
      );
      NotificationToast().deleted();
    } catch (error) {
      setSnackbar({
        open: true,
        message: '削除に失敗しました',
        severity: 'error',
      });
    }
  };

  /**
   * 日報とコメントを全て既読にする
   */
  const handleAllRead = async () => {
    try {
      setIsReading(true);
      await DailyReportReadModel.allMarkAsReadByDailyReport(dailyReport);
      setIsRead(true);

      NotificationToast().success('既読にしました');

      // 該当の日報を既読状態にする
      const updateDailyReports = dailyReports.map(e => {
        if (e.id === dailyReport.id) {
          // 本体とコメントを既読状態にする
          const update = e.comments.map(comment => {
            return {
              ...comment,
              is_read: true,
            };
          });
          setDailyReport({ ...e, is_read: true, comments: update });
          return { ...e, is_read: true, comments: update };
        }
        return e;
      });

      setDailyReports(updateDailyReports);
    } catch (error) {
      console.error('Failed to mark as read:', error);
      setSnackbar({
        open: true,
        message: '既読の設定に失敗しました',
        severity: 'error',
      });
    } finally {
      setIsReading(false);
    }
  };

  /**
   * 指定したコメントを既読にする
   * @param commentId
   */
  const onCommentRead = (commentId: number) => {
    const update = dailyReports.map(report => {
      if (report.id === dailyReport.id) {
        const update = report.comments.map(comment => {
          if (comment.id === commentId) {
            comment.is_read = true;
          }
          return comment;
        });
        console.log('既読', report);
        setDailyReport({ ...report, comments: update });
      }

      return report;
    });

    setDailyReports(update);
  };

  /**
   * コメントを末尾に追加する
   * @param dailyReportComment
   */
  const handleSendRead = (dailyReportComment: DailyReportComment) => {
    const update = dailyReports.map(report => {
      if (report.id === dailyReport.id) {
        report.comments.push(dailyReportComment);
        console.log('コメント追加', report);
        setDailyReport(report);
      }
      return report;
    });

    setDailyReports(update);
  };

  const handleCommentDelete = (commentId: number) => {
    const update = dailyReports.map(report => {
      if (report.id === dailyReport.id) {
        const update = report.comments.filter(comment => comment.id !== commentId);
        console.log('コメント削除', update);
        setDailyReport({ ...report, comments: update });
        return { ...report, comments: update };
      }
      return report;
    });
    console.log('コメント削除全体', update);
    setDailyReports(update);
  };

  /**コメントの開閉 */
  const toggleComments = () => {
    setShowComment(!showComment);
  };

  const onReceive = async (invoice: Invoice) => {
    // サーバーから最新の請求書データを取得
    const res = await receiptService.receipt({ id: invoice.id });

    const updatedInvoices = invoices.map(inv =>
      inv.id === res.id
        ? {
            ...inv, // 既存のプロパティはすべて残す
            state: res.state, // state プロパティだけ最新値に更新
          }
        : inv
    );

    // ステートを更新
    setInvoices(updatedInvoices);
  };

  const onReject = async (invoice: Invoice) => {
    // サーバーから最新の請求書データを取得
    const res = await receiptService.reject({ id: invoice.id });

    const updatedInvoices = invoices.map(inv =>
      inv.id === res.id
        ? {
            ...inv, // 既存のプロパティはすべて残す
            state: res.state, // state プロパティだけ最新値に更新
          }
        : inv
    );

    // ステートを更新
    setInvoices(updatedInvoices);
  };

  useEffect(() => {
    setShowComment(showComments);
  }, [showComments]);

  return (
    <>
      <Paper
        elevation={23}
        style={{ padding: '10px', backgroundColor: isRead ? '#eeeeee' : '#e7ffd9' }}
      >
        <h4>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" style={{ fontWeight: 'bold' }}>
              日報 {displayToYearMonthDay(dailyReport.ymd)}
            </Typography>
            {/* <ReadAllStatusButton
              onClick={handleAllRead}
              isRead={DailyReportUtil.isAllReadCheck(dailyReport)}
            /> */}
          </Box>

          <Typography variant="caption" display="block" gutterBottom color="textSecondary">
            {`投稿日:${displayDate(dailyReport.updated_at)}`}
          </Typography>
        </h4>
        <Typography variant="body1" display="block" gutterBottom style={{ fontWeight: 'bold' }}>
          {user?.name} ({user?.email})
        </Typography>

        <Paper className={classes.container}>
          <Typography
            variant="h5"
            display="block"
            gutterBottom
            color="error"
            style={{ fontWeight: 'bold' }}
          >
            {`本日の成果：${sumTotalAmount()}円`}
          </Typography>
          <Typography
            variant="body1"
            gutterBottom
            style={{
              color: dailyReport.memo ? undefined : '#ccc',
            }}
          >
            {dailyReport.memo ? formatCommentText(dailyReport.memo) : '所感はありません'}
          </Typography>

          <Box
            display="flex"
            justifyContent="space-between"
            style={{ marginLeft: '10px', marginRight: '10px' }}
          >
            <Box style={{}}>
              <Button
                variant="contained"
                color={!showComment ? 'primary' : 'inherit'}
                size="small"
                startIcon={<FontAwesome name="comment-o" size={24} color="#ffffff" />}
                onClick={toggleComments}
              >
                {`コメント:${comments.length}`}
              </Button>
            </Box>
            {!isMyDailyReport ? <></> : <DeleteButton onDelete={handleDelete} />}
          </Box>
          {/* 水平線 */}
          <Divider
            variant="middle"
            style={{
              backgroundColor: 'black',
              marginTop: '10px',
              marginBottom: '5px',
              borderWidth: '2px',
            }}
          />
          <div style={{}}>
            <DailyReportCommentList
              showComment={showComment}
              dailyReportComments={comments}
              dailyReport={dailyReport}
              onSendComment={handleSendRead}
              onCommentRead={onCommentRead}
              onCommentDelete={handleCommentDelete}
              componetCommentListBottom={
                <>
                  {/* 既読ボタン右側に設置 */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column', // 縦軸に変更
                      justifyContent: 'flex-start', // 上部に配置
                    }}
                  >
                    <ReadAllStatusButton
                      style={{ marginBottom: '8px', marginTop: '4px' }}
                      onClick={handleAllRead}
                      isRead={DailyReportUtil.isAllReadCheck(dailyReport)}
                    />
                  </div>
                </>
              }
            />
          </div>
        </Paper>

        {/* スクロール可能なコンテナ */}
        <div
          style={{
            maxWidth: '100%',
            overflow: 'auto',
            backgroundColor: '#e0f7da',
          }}
        >
          <DailyReportInvoiceList invoices={invoices} onReceive={onReceive} onReject={onReject} />
        </div>
      </Paper>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DailyReportItem;
